/**
 * Presentation reducer for a Case Partner turn.
 *
 * Must stay aligned with ``TurnSnapshot.apply`` in
 * ``backend/app/modules/case_partner/turn_snapshot.py``. Both are locked by
 * ``case_partner_turn_reducer.fixture.json``.
 */

import { mergeUiChanges, normalizeUiChanges, type UiChange } from '@/lib/uiChangeBus';

export const FILE_OUTPUT_TOOLS = new Set([
  'read_files',
  'delegate_document_analysis',
  'search_documents',
]);

const toolExecutedStatus = (payload: Record<string, any>): 'completed' | 'failed' => (
  payload.success === false ? 'failed' : 'completed'
);

// A nested tape lands in the lead's JSONB row, so it is bounded. Commentary is
// clipped per step rather than dropped: a truncated sentence still reads.
export const MAX_AGENT_CHILD_STEPS = 60;

// One entry per agent the lead can spawn. A parent id that never turns up
// would otherwise accumulate for the life of the turn.
export const MAX_HELD_AGENT_PARENTS = 8;
export const MAX_AGENT_COMMENTARY_CHARS = 2000;

// Tool statuses that claim the call is running right now. Anything else is a
// record of how it ended.
const UNFINISHED_TOOL_STATUSES = new Set(['pending', 'in_progress']);

const spawnOutputStillRunning = (data: { function?: { name?: string; output?: unknown } }): boolean => {
  const name = data.function?.name;
  if (name !== 'spawn_research' && name !== 'spawn_web_research') return false;
  const raw = data.function?.output;
  if (!raw) return false;
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return (raw as { status?: unknown }).status === 'running';
  }
  if (typeof raw !== 'string') return false;
  try {
    const parsed = JSON.parse(raw) as unknown;
    return Boolean(
      parsed
      && typeof parsed === 'object'
      && !Array.isArray(parsed)
      && (parsed as { status?: unknown }).status === 'running',
    );
  } catch {
    return false;
  }
};

const toolRowStillOpen = (
  data: { status?: string; function?: { name?: string; output?: unknown } },
): boolean => {
  if (UNFINISHED_TOOL_STATUSES.has(data.status ?? '')) return true;
  if (data.status === 'stopped' || data.status === 'failed') return false;
  return spawnOutputStillRunning(data);
};

/**
 * Record tool calls that never finished.
 *
 * Mirrors ``seal_unfinished_steps`` in ``turn_snapshot.py``. The client ends a
 * stopped turn from what it rendered rather than waiting for the server, so it
 * has to seal the same way or the turn would show its agents working until the
 * next reload swapped in the server's copy.
 */
export const sealUnfinishedSteps = <T extends { type?: string; data?: any }>(
  steps: T[],
): T[] => steps.map((step) => {
  if (step.type !== 'tool' || !step.data) return step;
  const { children } = step.data;
  return {
    ...step,
    data: {
      ...step.data,
      ...(toolRowStillOpen(step.data) ? { status: 'stopped' } : {}),
      // An agent's own tools sit under its spawn call and stop with it.
      ...(Array.isArray(children) ? { children: sealUnfinishedSteps(children) } : {}),
    },
  };
});

/**
 * Drop waits the stop interrupted.
 *
 * Mirrors ``seal_stopped_transcript`` in ``turn_snapshot.py``. A wait that never
 * ended has no duration to report, and left open it renders as a turn still
 * waiting on agents that will never report — with a shimmer, forever, because a
 * finalized turn receives no further events.
 */
/**
 * Drop waits the turn never finished, for a turn that has now ended.
 *
 * A finished turn has no open wait, so every path that writes a terminal
 * message applies this. It is also what `timelineWaitMarkersFromMetadata`
 * enforces on reload by keeping only markers with a duration: without it here,
 * an interrupted turn shimmers "Waiting for agents to finish..." until the
 * lawyer reloads and the same turn renders differently.
 */
export const closeOpenWaitMarkers = <T extends { waiting_for?: string | null }>(
  markers: T[] | undefined,
): T[] => (markers ?? []).filter((marker) => !marker.waiting_for);

/**
 * What Stop must do to the live turn before the persist POST.
 *
 * `liveStreaming` flips off on the click, so spawn `{status:"running"}` would
 * otherwise render as complete while wait chips still say the turn is waiting.
 * Seal the in-memory presentation here; the terminal message path seals again
 * the same way and is a no-op on already-stopped rows.
 */
export const sealLiveTurnPresentation = <
  S extends { type?: string; data?: any },
  M extends { waiting_for?: string | null },
>(
  steps: S[],
  waitMarkers: M[] | undefined,
): { steps: S[]; waitMarkers: M[] } => ({
  steps: steps.length > 0 ? sealUnfinishedSteps(steps) : steps,
  waitMarkers: closeOpenWaitMarkers(waitMarkers),
});

export const IGNORED_TURN_EVENT_TYPES = new Set([
  'ping',
  'complete',
  'stopped',
  'error',
  'replay_complete',
  'snapshot',
]);

export type TurnThinkingStep = {
  type: 'reasoning' | 'tool' | 'text';
  round_num?: number;
  summary_index?: number;
  data: Record<string, any>;
};

export type TurnLeadWaitMarker = {
  id: string;
  round_num: number;
  waiting_for?: string;
  duration_ms?: number;
};

export type TurnReducerState = {
  v: 1;
  content: string;
  current_round: number | null;
  round_start: number;
  rounds_with_tools: number[];
  thinking_steps: TurnThinkingStep[];
  text_segments?: Array<{
    round_num: number;
    text: string;
    message_id?: string;
    message_phase?: 'commentary' | 'final_answer';
  }>;
  bundle?: Record<string, string>;
  deadline?: Record<string, string>;
  ui_changes?: UiChange[];
  draft_metadata?: Record<string, any>;
  final_answer_confirmed?: boolean;
  /** Server clock for the whole turn, frozen when the answer was confirmed. */
  reasoning_duration_ms?: number;
  lead_waiting_for?: string;
  lead_waited_duration_ms?: number;
  lead_wait_markers?: TurnLeadWaitMarker[];
  /**
   * Agent events whose chip has not arrived yet. Mirrors
   * `_held_agent_events` in turn_snapshot.py. Serialized so a reconnect whose
   * snapshot cursor has already passed them can still fold them when the spawn
   * chip lands.
   */
  held_agent_events?: Record<string, Record<string, any>[]>;
};

export const emptyTurnReducerState = (): TurnReducerState => ({
  v: 1,
  content: '',
  current_round: null,
  round_start: 0,
  rounds_with_tools: [],
  thinking_steps: [],
});

export function thinkingStepKey(step: TurnThinkingStep): string {
  if (step.type === 'tool') {
    return `tool-${step.round_num ?? 0}-${step.data.call_id ?? ''}`;
  }
  if (step.type === 'text') {
    return `text-${step.round_num ?? 0}-${step.data.message_id || 'text'}`;
  }
  return `reasoning-${step.round_num ?? 0}-${step.summary_index ?? 0}`;
}

const STEP_TYPES = new Set(['reasoning', 'tool', 'text']);

export function thinkingStepsFromSnapshot(raw: unknown): TurnThinkingStep[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((item): item is TurnThinkingStep => (
    Boolean(item)
    && typeof item === 'object'
    && STEP_TYPES.has((item as TurnThinkingStep).type)
  ));
}

/**
 * Ordered thinking steps for one agent.
 *
 * The lead owns the outer tape and every background agent owns a tape nested
 * under its own tool call. Both hold the same step shape, so a tool renders
 * identically wherever it sits and a new tool needs no work to appear. These
 * mutate the array in place; callers clone first.
 */
type StepTape = TurnThinkingStep[];

function attachStep(tape: StepTape, step: TurnThinkingStep, limit?: number): void {
  if (limit != null && tape.length >= limit) return;
  tape.push(step);
}

function upsertToolInTape(
  tape: StepTape,
  payload: Record<string, any>,
  status: string,
  limit?: number,
): TurnThinkingStep {
  const roundNum = Number(payload.round_num || 0);
  const callId = String(payload.call_id || '');
  const key = `tool-${roundNum}-${callId}`;
  const existing = tape.find((step) => thinkingStepKey(step) === key);
  if (!existing) {
    const step: TurnThinkingStep = {
      type: 'tool',
      round_num: roundNum,
      summary_index: 999,
      data: {
        call_id: callId,
        round_num: roundNum,
        displayInfo: payload.tool_display_info ?? null,
        function: {
          name: String(payload.function_name || ''),
          arguments: String(payload.function_arguments || ''),
          output: '',
        },
        status,
      },
    };
    if (typeof payload.tool_family === 'string' && payload.tool_family) {
      step.data.family = payload.tool_family;
    }
    attachStep(tape, step, limit);
    return step;
  }
  const data = existing.data;
  data.status = status;
  if (payload.function_name) {
    data.function.name = String(payload.function_name);
  }
  if (payload.function_arguments) {
    data.function.arguments = String(payload.function_arguments);
  }
  if (payload.tool_display_info) {
    data.displayInfo = payload.tool_display_info;
  }
  if (typeof payload.tool_family === 'string' && payload.tool_family) {
    data.family = payload.tool_family;
  }
  return existing;
}

function completeToolArgsInTape(
  tape: StepTape,
  payload: Record<string, any>,
  limit?: number,
): void {
  const step = upsertToolInTape(tape, payload, 'in_progress', limit);
  if (payload.function_arguments) {
    step.data.function.arguments = String(payload.function_arguments);
  }
  if (payload.tool_display_info) {
    step.data.displayInfo = payload.tool_display_info;
  }
}

function executeToolInTape(
  tape: StepTape,
  payload: Record<string, any>,
  limit?: number,
): TurnThinkingStep {
  const step = upsertToolInTape(tape, payload, toolExecutedStatus(payload), limit);
  const functionName = String(payload.function_name || step.data.function.name);
  step.data.function.name = functionName;
  if (payload.function_output) {
    step.data.function.output = String(payload.function_output);
  }
  if (FILE_OUTPUT_TOOLS.has(functionName) && payload.files) {
    step.data.function.output = JSON.stringify(payload.files);
  }
  if (payload.tool_display_info) {
    step.data.displayInfo = payload.tool_display_info;
  }
  return step;
}

function applyReasoningToTape(tape: StepTape, payload: Record<string, any>, limit?: number): void {
  const summaryIndex = Number(payload.summary_index || 0);
  const roundNum = Number(payload.round_num || 0);
  const key = `reasoning-${roundNum}-${summaryIndex}`;
  let step = tape.find((item) => thinkingStepKey(item) === key);
  if (!step) {
    step = {
      type: 'reasoning',
      round_num: roundNum,
      summary_index: summaryIndex,
      data: {
        text: '',
        isComplete: false,
        summary_index: summaryIndex,
        round_num: roundNum,
      },
    };
    attachStep(tape, step, limit);
  }
  if (typeof payload.reasoning === 'string' && payload.reasoning) {
    step.data.text = String(step.data.text || '') + payload.reasoning;
  }
  if (payload.reasoning_complete) {
    step.data.isComplete = true;
  }
}

/**
 * Record how long a round's thinking took, on the last of its summaries.
 *
 * Mirrors `apply_reasoning_duration` in turn_snapshot.py. The figure describes
 * the stretch as a whole, so it sits on one step rather than on each: a reader
 * adding up what a chip spent thinking would otherwise count the same stretch
 * once per summary it arrived in. A round whose thinking produced no summary has
 * nothing to carry it, and nothing on screen to report it either.
 */
function applyReasoningDurationToTape(
  tape: StepTape,
  roundNum: number,
  durationMs: number,
): void {
  for (let index = tape.length - 1; index >= 0; index -= 1) {
    const step = tape[index];
    if (step.type !== 'reasoning') continue;
    if (Number(step.round_num ?? 0) !== roundNum) continue;
    step.data.duration_ms = durationMs;
    return;
  }
}

/** Agent commentary, interleaved with that agent's tools. */
function appendTextToTape(
  tape: StepTape,
  payload: Record<string, any>,
  text: string,
  limit?: number,
): void {
  const roundNum = Number(payload.round_num || 0);
  const messageId = typeof payload.message_id === 'string' && payload.message_id
    ? payload.message_id
    : undefined;
  const key = `text-${roundNum}-${messageId || 'text'}`;
  let step = tape.find((item) => thinkingStepKey(item) === key);
  if (!step) {
    step = {
      type: 'text',
      round_num: roundNum,
      summary_index: 999,
      data: { text: '', round_num: roundNum },
    };
    if (messageId) {
      step.data.message_id = messageId;
    }
    attachStep(tape, step, limit);
  }
  step.data.text = (String(step.data.text || '') + text).slice(0, MAX_AGENT_COMMENTARY_CHARS);
}

/**
 * Relabel the newest still-running tool. Returns true when one matched.
 *
 * A progress frame has to name what it is about, by call_id or function_name.
 * One that names neither is discarded rather than guessed at, which would
 * relabel whatever tool happened to be running.
 */
function applyProgressToTape(
  tape: StepTape,
  payload: Record<string, any>,
  label: string,
): boolean {
  const callId = typeof payload.call_id === 'string' ? payload.call_id : '';
  const functionName = typeof payload.function_name === 'string' ? payload.function_name : '';
  if (!callId && !functionName) return false;
  for (const step of tape) {
    if (step.type !== 'tool') continue;
    if (step.data.status !== 'pending' && step.data.status !== 'in_progress') continue;
    if (callId) {
      if (step.data?.call_id !== callId) continue;
    } else if (step.data?.function?.name !== functionName) {
      continue;
    }
    if (typeof payload.objective === 'string' && payload.objective.trim()) {
      step.data.objective = payload.objective.trim();
    }
    step.data.displayInfo = label;
    return true;
  }
  return false;
}

function progressLabel(payload: Record<string, any>): string {
  const stepLabel = typeof payload.step === 'string' ? payload.step.trim() : '';
  if (stepLabel) return stepLabel;
  return typeof payload.message === 'string' ? payload.message.trim() : '';
}

function cloneStep(step: TurnThinkingStep): TurnThinkingStep {
  const data: Record<string, any> = { ...step.data };
  if (step.data.function && typeof step.data.function === 'object') {
    data.function = { ...step.data.function };
  }
  if (Array.isArray(step.data.children)) {
    data.children = step.data.children.map(cloneStep);
  }
  return { ...step, data };
}

function cloneState(state: TurnReducerState): TurnReducerState {
  const next: TurnReducerState = {
    v: 1,
    content: state.content,
    current_round: state.current_round,
    round_start: state.round_start,
    rounds_with_tools: [...state.rounds_with_tools],
    thinking_steps: state.thinking_steps.map(cloneStep),
    text_segments: state.text_segments?.map((segment) => ({ ...segment })),
  };
  if (state.bundle) {
    next.bundle = { ...state.bundle };
  }
  if (state.deadline) {
    next.deadline = { ...state.deadline };
  }
  if (state.ui_changes) {
    next.ui_changes = state.ui_changes.map((change) => ({ ...change }));
  }
  if (state.draft_metadata) {
    next.draft_metadata = { ...state.draft_metadata };
  }
  if (state.final_answer_confirmed) {
    next.final_answer_confirmed = true;
  }
  if (state.reasoning_duration_ms) {
    next.reasoning_duration_ms = state.reasoning_duration_ms;
  }
  if (state.lead_waiting_for) {
    next.lead_waiting_for = state.lead_waiting_for;
  }
  if (state.lead_waited_duration_ms) {
    next.lead_waited_duration_ms = state.lead_waited_duration_ms;
  }
  if (state.lead_wait_markers) {
    next.lead_wait_markers = state.lead_wait_markers.map((marker) => ({ ...marker }));
  }
  if (state.held_agent_events) {
    next.held_agent_events = Object.fromEntries(
      Object.entries(state.held_agent_events).map(([parentCallId, events]) => (
        [parentCallId, [...events]]
      )),
    );
  }
  return next;
}

function sortedRounds(rounds: Iterable<number>): number[] {
  return [...new Set(rounds)].sort((left, right) => left - right);
}

export function positiveDurationMs(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return undefined;
  }
  return Math.round(value);
}

export function reasoningDurationMetadata(args: {
  hasSteps: boolean;
  stateDurationMs?: number | null;
  frozenDurationMs?: number | null;
  turnStartedAt?: number | null;
  now?: number;
}): { reasoning_duration_ms: number } | Record<string, never> {
  if (!args.hasSteps) {
    return {};
  }
  const elapsed = args.turnStartedAt != null
    ? Math.max(1, (args.now ?? Date.now()) - args.turnStartedAt)
    : undefined;
  const durationMs = (
    positiveDurationMs(args.stateDurationMs)
    ?? positiveDurationMs(args.frozenDurationMs)
    ?? positiveDurationMs(elapsed)
  );
  if (durationMs == null) {
    return {};
  }
  return { reasoning_duration_ms: durationMs };
}

function applyTurnReasoningDuration(
  state: TurnReducerState,
  payload: Record<string, any>,
): void {
  const durationMs = positiveDurationMs(payload.reasoning_duration_ms);
  if (durationMs != null) {
    state.reasoning_duration_ms = durationMs;
  }
}

export function turnReducerFromSnapshot(data: Record<string, any> | null | undefined): TurnReducerState {
  const state = emptyTurnReducerState();
  if (!data || typeof data !== 'object') {
    return state;
  }
  state.content = String(data.content || '');
  if (Array.isArray(data.text_segments)) {
    state.text_segments = data.text_segments.flatMap((segment: unknown) => {
      if (!segment || typeof segment !== 'object') return [];
      const value = segment as Record<string, unknown>;
      const text = typeof value.text === 'string' ? value.text : '';
      return text
        ? [{
            round_num: Number(value.round_num || 0),
            text,
            ...(typeof value.message_id === 'string' && value.message_id
              ? { message_id: value.message_id }
              : {}),
            ...(value.message_phase === 'commentary' || value.message_phase === 'final_answer'
              ? { message_phase: value.message_phase }
              : {}),
          }]
        : [];
    });
  }
  if (typeof data.current_round === 'number') {
    state.current_round = data.current_round;
  }
  if (typeof data.round_start === 'number' && data.round_start >= 0) {
    state.round_start = Math.min(data.round_start, state.content.length);
  } else if (state.current_round !== null) {
    state.round_start = state.content.length;
  }
  if (Array.isArray(data.rounds_with_tools)) {
    state.rounds_with_tools = sortedRounds(
      data.rounds_with_tools
        .map((roundNum: unknown) => (
          typeof roundNum === 'number' ? roundNum : Number.parseInt(String(roundNum), 10)
        ))
        .filter((roundNum: number) => Number.isInteger(roundNum)),
    );
  }
  if (Array.isArray(data.thinking_steps)) {
    data.thinking_steps.forEach((step: unknown) => {
      if (!step || typeof step !== 'object') return;
      state.thinking_steps.push(step as TurnThinkingStep);
    });
  }
  if (data.deadline && typeof data.deadline === 'object') {
    state.deadline = Object.fromEntries(
      Object.entries(data.deadline as Record<string, unknown>)
        .filter(([, value]) => value != null)
        .map(([key, value]) => [key, String(value)]),
    );
  }
  if (data.bundle && typeof data.bundle === 'object') {
    state.bundle = Object.fromEntries(
      Object.entries(data.bundle)
        .filter(([, value]) => value != null)
        .map(([key, value]) => [key, String(value)]),
    );
  }
  if (Array.isArray(data.ui_changes)) {
    state.ui_changes = mergeUiChanges(state.ui_changes, normalizeUiChanges({ ui_changes: data.ui_changes }));
  }
  if (data.draft_metadata && typeof data.draft_metadata === 'object') {
    state.draft_metadata = data.draft_metadata;
  }
  if (data.final_answer_confirmed) {
    state.final_answer_confirmed = true;
  }
  applyTurnReasoningDuration(state, data);
  if (typeof data.lead_waiting_for === 'string' && data.lead_waiting_for.trim()) {
    state.lead_waiting_for = data.lead_waiting_for.trim();
  }
  if (typeof data.lead_waited_duration_ms === 'number' && data.lead_waited_duration_ms > 0) {
    state.lead_waited_duration_ms = data.lead_waited_duration_ms;
  }
  if (Array.isArray(data.lead_wait_markers)) {
    state.lead_wait_markers = data.lead_wait_markers
      .filter((marker: unknown) => marker && typeof marker === 'object')
      .map((marker: Record<string, any>, index: number) => ({
        id: typeof marker.id === 'string' ? marker.id : `wait-${index + 1}`,
        round_num: Number(marker.round_num || 0),
        ...(typeof marker.waiting_for === 'string' && marker.waiting_for
          ? { waiting_for: marker.waiting_for }
          : {}),
        ...(typeof marker.duration_ms === 'number' && marker.duration_ms > 0
          ? { duration_ms: marker.duration_ms }
          : {}),
      }));
  }
  const held = heldAgentEventsFromSnapshot(data.held_agent_events);
  if (held) {
    for (const parentCallId of Object.keys(held)) {
      if (state.thinking_steps.some((step) => (
        step.type === 'tool' && step.data?.call_id === parentCallId
      ))) {
        delete held[parentCallId];
      }
    }
    if (Object.keys(held).length) {
      state.held_agent_events = held;
    }
  }
  return state;
}

function heldAgentEventsFromSnapshot(
  raw: unknown,
): Record<string, Record<string, any>[]> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }
  const held: Record<string, Record<string, any>[]> = {};
  for (const [parentCallId, events] of Object.entries(raw)) {
    if (!parentCallId || !Array.isArray(events)) continue;
    if (Object.keys(held).length >= MAX_HELD_AGENT_PARENTS) break;
    const copied = events
      .filter((item): item is Record<string, any> => (
        Boolean(item) && typeof item === 'object' && !Array.isArray(item)
      ))
      .slice(0, MAX_AGENT_CHILD_STEPS)
      .map((item) => ({ ...item }));
    if (copied.length) {
      held[parentCallId] = copied;
    }
  }
  return Object.keys(held).length ? held : undefined;
}

function mergeDraftMetadata(
  current: Record<string, any> | undefined,
  incoming: Record<string, any>,
): Record<string, any> {
  const row = Object.fromEntries(
    Object.entries(incoming).filter(([key]) => (
      key.startsWith('draft_document_') && key !== 'draft_documents'
    )),
  );
  if (Object.keys(row).length === 0) {
    return current ?? incoming;
  }
  if (!current) {
    return { ...incoming };
  }
  let rows: Record<string, any>[];
  if (Array.isArray(current.draft_documents)) {
    rows = current.draft_documents.filter((item) => item && typeof item === 'object');
  } else {
    const existing = Object.fromEntries(
      Object.entries(current).filter(([key]) => (
        key.startsWith('draft_document_') && key !== 'draft_documents'
      )),
    );
    rows = Object.keys(existing).length > 0 ? [existing] : [];
  }
  const rowKey = row.draft_document_job_guid || row.draft_document_guid;
  rows = rows.filter((item) => (
    (item.draft_document_job_guid || item.draft_document_guid) !== rowKey
  ));
  rows.push(row);
  if (rows.length === 1) {
    return { ...current, ...rows[0] };
  }
  const merged = Object.fromEntries(
    Object.entries(current).filter(([key]) => key !== 'draft_documents' && !key.startsWith('draft_document_')),
  );
  merged.draft_documents = rows;
  return merged;
}

/**
 * Fold an agent's event into the tape under its own tool call.
 *
 * Nothing here touches lead state: an agent's tools must not mark the lead's
 * round as a tool round, nor unconfirm its final answer.
 */
/**
 * Keep an agent's event until the chip it belongs under exists.
 *
 * Mirrors `_hold_agent_event` in turn_snapshot.py. The lead reaches the log
 * through an in-process queue and a drainer while its agents publish straight
 * to Redis from another worker, so parent-first is what normally happens
 * between the two, not what they guarantee. Held events are part of the
 * snapshot so a reconnect whose cursor has already passed them can still fold
 * them when the chip lands.
 */
function holdAgentEvent(
  state: TurnReducerState,
  parentCallId: string,
  payload: Record<string, any>,
): TurnReducerState {
  const held = state.held_agent_events ?? {};
  const forParent = held[parentCallId];
  if (!forParent && Object.keys(held).length >= MAX_HELD_AGENT_PARENTS) return state;
  if (forParent && forParent.length >= MAX_AGENT_CHILD_STEPS) return state;
  state.held_agent_events = { ...held, [parentCallId]: [...(forParent ?? []), payload] };
  return state;
}

function releaseAgentEvents(
  state: TurnReducerState,
  parentCallId: string,
): TurnReducerState {
  const pending = state.held_agent_events;
  const held = pending?.[parentCallId];
  if (!pending || !held) return state;
  delete pending[parentCallId];
  for (const payload of held) {
    applyNestedEvent(state, parentCallId, payload);
  }
  return state;
}

function applyNestedEvent(
  state: TurnReducerState,
  parentCallId: string,
  payload: Record<string, any>,
): TurnReducerState | null {
  const parent = state.thinking_steps.find((step) => (
    step.type === 'tool' && step.data?.call_id === parentCallId
  ));
  if (!parent) return holdAgentEvent(state, parentCallId, payload);
  const tape: StepTape = Array.isArray(parent.data.children) ? parent.data.children : [];
  const limit = MAX_AGENT_CHILD_STEPS;
  if (payload.tool_call_started) {
    upsertToolInTape(tape, payload, 'pending', limit);
  } else if (payload.tool_call_completed) {
    completeToolArgsInTape(tape, payload, limit);
  } else if (payload.tool_executed) {
    executeToolInTape(tape, payload, limit);
  } else if (payload.type === 'delegate_progress') {
    const label = progressLabel(payload);
    if (!label || !applyProgressToTape(tape, payload, label)) return null;
  } else if (typeof payload.text === 'string' && payload.text) {
    appendTextToTape(tape, payload, payload.text, limit);
  } else {
    return null;
  }
  if (tape.length) {
    parent.data.children = tape;
  }
  return state;
}

export function applyTurnEvent(
  input: TurnReducerState,
  payload: Record<string, any> | null | undefined,
): TurnReducerState {
  if (!payload || typeof payload !== 'object') {
    return input;
  }
  const eventType = payload.type;
  if (typeof eventType === 'string' && IGNORED_TURN_EVENT_TYPES.has(eventType)) {
    return input;
  }

  const state = cloneState(input);

  const parentCallId = payload.parent_call_id;
  if (typeof parentCallId === 'string' && parentCallId) {
    return applyNestedEvent(state, parentCallId, payload) ?? input;
  }

  if (eventType === 'lead_waiting') {
    if (typeof payload.waiting_for === 'string' && payload.waiting_for.trim()) {
      state.lead_waiting_for = payload.waiting_for.trim();
      const markers = state.lead_wait_markers ?? [];
      state.lead_wait_markers = [
        ...markers,
        {
          id: `wait-${markers.length + 1}`,
          round_num: Number(payload.round_num || 0),
          waiting_for: payload.waiting_for.trim(),
        },
      ];
    } else {
      delete state.lead_waiting_for;
    }
    if (typeof payload.duration_ms === 'number' && payload.duration_ms > 0) {
      state.lead_waited_duration_ms = (state.lead_waited_duration_ms ?? 0) + payload.duration_ms;
      const markers = state.lead_wait_markers ?? [];
      const openIndex = markers.findLastIndex((marker) => Boolean(marker.waiting_for));
      if (openIndex >= 0) {
        state.lead_wait_markers = markers.map((marker, index) => (
          index === openIndex
            ? {
                id: marker.id,
                round_num: marker.round_num,
                duration_ms: payload.duration_ms,
              }
            : marker
        ));
      } else {
        state.lead_wait_markers = [
          ...markers,
          {
            id: `wait-${markers.length + 1}`,
            round_num: Number(payload.round_num || 0),
            duration_ms: payload.duration_ms,
          },
        ];
      }
    }
    return state;
  }

  if (eventType === 'final_answer_confirmed' || payload.final_answer_confirmed) {
    state.final_answer_confirmed = true;
    applyTurnReasoningDuration(state, payload);
    return state;
  }

  if (payload.tool_call_started) {
    const roundNum = Number(payload.round_num || 0);
    const rounds = new Set(state.rounds_with_tools);
    rounds.add(roundNum);
    state.rounds_with_tools = sortedRounds(rounds);
    delete state.final_answer_confirmed;
    delete state.reasoning_duration_ms;
    upsertToolInTape(state.thinking_steps, payload, 'pending');
    return releaseAgentEvents(state, String(payload.call_id ?? ''));
  }

  if (payload.tool_call_completed) {
    completeToolArgsInTape(state.thinking_steps, payload);
    return state;
  }

  if (payload.tool_executed) {
    executeToolInTape(state.thinking_steps, payload);
    const metadata = payload.metadata;
    if (metadata && typeof metadata === 'object') {
      if (metadata.bundle_url) {
        state.bundle = {
          bundle_url: String(metadata.bundle_url),
          bundle_title: String(metadata.bundle_title || 'Bundle'),
        };
      }
      if (metadata.deadline_url) {
        state.deadline = {
          deadline_url: String(metadata.deadline_url),
          deadline_title: String(metadata.deadline_title || 'Deadline'),
        };
      }
      const incomingChanges = normalizeUiChanges(metadata as Record<string, unknown>);
      if (incomingChanges.length > 0) {
        state.ui_changes = mergeUiChanges(state.ui_changes, incomingChanges);
      }
      if (
        metadata.draft_document_action !== 'inspect'
        && (metadata.draft_document_guid || metadata.draft_document_job_guid)
      ) {
        state.draft_metadata = mergeDraftMetadata(state.draft_metadata, metadata);
      }
    }
    return state;
  }

  if (eventType === 'delegate_progress') {
    const label = progressLabel(payload);
    if (!label || !applyProgressToTape(state.thinking_steps, payload, label)) {
      return input;
    }
    return state;
  }

  if (eventType === 'reasoning_duration') {
    if (typeof payload.duration_ms === 'number' && payload.duration_ms > 0) {
      applyReasoningDurationToTape(
        state.thinking_steps,
        Number(payload.round_num || 0),
        payload.duration_ms,
      );
    }
    return state;
  }

  if (
    payload.reasoning_part_added
    || payload.reasoning != null
    || payload.reasoning_complete
  ) {
    applyReasoningToTape(state.thinking_steps, payload);
    return state;
  }

  const text = payload.text;
  if (typeof text === 'string' && text) {
    const roundNum = Number(payload.round_num || 0);
    if (state.rounds_with_tools.includes(roundNum)) {
      return input;
    }
    if (state.current_round !== roundNum) {
      state.current_round = roundNum;
      state.round_start = state.content.length;
    }
    const messageId = typeof payload.message_id === 'string' && payload.message_id
      ? payload.message_id
      : undefined;
    const messagePhase = payload.message_phase === 'commentary' || payload.message_phase === 'final_answer'
      ? payload.message_phase
      : undefined;
    const lastSegment = state.text_segments?.[state.text_segments.length - 1];
    const sameSegment = Boolean(lastSegment) && (
      messageId
        ? lastSegment?.message_id === messageId
        : (
            !lastSegment?.message_id
            && lastSegment?.round_num === roundNum
            && lastSegment?.message_phase === messagePhase
          )
    );
    if (sameSegment && lastSegment) {
      lastSegment.text += text;
    } else {
      state.text_segments = [
        ...(state.text_segments ?? []),
        {
          round_num: roundNum,
          text,
          ...(messageId ? { message_id: messageId } : {}),
          ...(messagePhase ? { message_phase: messagePhase } : {}),
        },
      ];
      if (state.content && lastSegment) {
        state.content += '\n\n';
      }
    }
    if (messagePhase === 'final_answer') {
      state.final_answer_confirmed = true;
    }
    state.content += text;
    return state;
  }

  return input;
}

export function turnReducerToSnapshot(state: TurnReducerState): Record<string, any> {
  const payload: Record<string, any> = {
    v: 1,
    content: state.content,
    current_round: state.current_round,
    round_start: state.round_start,
    rounds_with_tools: sortedRounds(state.rounds_with_tools),
    thinking_steps: state.thinking_steps,
    text_segments: state.text_segments,
  };
  if (state.bundle) {
    payload.bundle = state.bundle;
  }
  if (state.deadline) {
    payload.deadline = state.deadline;
  }
  if (state.ui_changes?.length) {
    payload.ui_changes = state.ui_changes;
  }
  if (state.draft_metadata) {
    payload.draft_metadata = state.draft_metadata;
  }
  if (state.final_answer_confirmed) {
    payload.final_answer_confirmed = true;
  }
  if (state.reasoning_duration_ms) {
    payload.reasoning_duration_ms = state.reasoning_duration_ms;
  }
  if (state.lead_waiting_for) {
    payload.lead_waiting_for = state.lead_waiting_for;
  }
  if (state.lead_waited_duration_ms) {
    payload.lead_waited_duration_ms = state.lead_waited_duration_ms;
  }
  if (state.lead_wait_markers?.length) {
    payload.lead_wait_markers = state.lead_wait_markers;
  }
  if (state.held_agent_events && Object.keys(state.held_agent_events).length) {
    payload.held_agent_events = Object.fromEntries(
      Object.entries(state.held_agent_events).map(([parentCallId, events]) => (
        [parentCallId, events.map((event) => ({ ...event }))]
      )),
    );
  }
  return payload;
}
